import { ENTER_MANUALLY_OPTION, NONE_OF_THE_ABOVE_OPTION } from "./constants";

/** Inputs used to classify a primary Dx save for analytics. */
export interface DiagnosisOutcomeInput {
  icdCode: string;
  defaultPrimaryCode?: string;
  dropDownOptions?: { value: string; label: string }[];
}

/** Analytics classification returned after a primary Dx save. */
export interface DiagnosisOutcome {
  outcome: string;
  stack_dx_code: string;
  user_dx_code: string;
  /** True when the final code was an on-chart option (NCCN/CMS chart row). */
  was_on_chart: boolean;
}

/**
 * Returns whether the user selected a different primary Dx than the stack default.
 *
 * @param outcome - Classification from {@link computeDiagnosisOutcome}.
 * @returns True when stack and user Dx both exist and differ.
 */
export const hasDxChanged = (outcome: DiagnosisOutcome): boolean =>
  !!outcome.stack_dx_code && outcome.stack_dx_code !== outcome.user_dx_code;

/**
 * Chart dropdown values only — excludes manual-entry / none-of-the-above sentinels.
 *
 * @param dropDownOptions - Full primary Dx select options including sentinels.
 * @return On-chart ICD code values.
 */
export const getChartDxOptionValues = (
  dropDownOptions?: { value: string; label: string }[],
): string[] =>
  (dropDownOptions ?? [])
    .map((option) => option.value)
    .filter(
      (value) =>
        !!value &&
        value !== ENTER_MANUALLY_OPTION &&
        value !== NONE_OF_THE_ABOVE_OPTION,
    );

/**
 * Classifies the user's primary Dx selection against the stack default and
 * dropdown options for analytics reporting.
 *
 * Outcome precedence:
 * 1. stack_dx == user_dx → stack_correct
 * 2. stack_dx is null/none → no_stack_user_added | no_stack_query_raised
 * 3. user_dx in chart options → stack_wrong_user_corrected
 * 4. user_dx empty with stack present → stack_wrong_query_raised
 * 5. else → user_added_off_chart
 *
 * `was_on_chart` is orthogonal to outcome (agreement vs chart provenance).
 *
 * @param input - Resolved ICD code, stack default, and dropdown options.
 * @return Outcome label, stack/user Dx codes, and on-chart flag.
 */
export const computeDiagnosisOutcome = ({
  icdCode,
  defaultPrimaryCode,
  dropDownOptions,
}: DiagnosisOutcomeInput): DiagnosisOutcome => {
  const stackDxCode = (defaultPrimaryCode ?? "").trim();
  const userDxCode = (icdCode ?? "").trim();
  const chartValues = getChartDxOptionValues(dropDownOptions);
  const wasOnChart =
    userDxCode !== "" && chartValues.some((value) => value === userDxCode);

  const baseOutcome = {
    stack_dx_code: stackDxCode,
    user_dx_code: userDxCode,
    was_on_chart: wasOnChart,
  };

  if (stackDxCode && userDxCode && stackDxCode === userDxCode) {
    return { ...baseOutcome, outcome: "stack_correct" };
  }

  if (!stackDxCode) {
    return {
      ...baseOutcome,
      outcome: userDxCode ? "no_stack_user_added" : "no_stack_query_raised",
    };
  }

  if (wasOnChart) {
    return { ...baseOutcome, outcome: "stack_wrong_user_corrected" };
  }

  if (!userDxCode) {
    return { ...baseOutcome, outcome: "stack_wrong_query_raised" };
  }

  return { ...baseOutcome, outcome: "user_added_off_chart" };
};
